import {
  PurchaseButtonProps,
  PurchaseButtonStateProps
} from '../../types/components/products/PurchaseButton';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import { User } from '../../types/api/UsersTypes';
import { ApplicationState } from '../../types/state/storeTypes';

import { addMessage } from '../../state/actions/messagesActions';
import { addNotification } from '../../state/actions/notificationsActions';
import { decrementAvailableCredits } from '../../state/actions/creditActions';
import { showModal } from '../../state/actions/buyMoreCreditsActions';

import { connect, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { useState } from 'react';

import purchaseHelpers from '../../helpers/purchaseHelpers';

import { QUERY_PARAMS as ALGOLIA_QUERY_PARAMS } from '../../constants/algolia';

export const productGridItemClasses = 'mt-[18px] py-.5 lg:py-1 px-2 lg:px-5 bg-a-blue inter text-12 lg:text-14 leading-22 font-medium';
export const collectionPageClasses =
  'py-4 px-[30px] bg-a-green eurostile text-17 leading-24 font-bold uppercase';
export const productModalClasses =
  'py-4 px-[30px] !bg-a-green eurostile text-17 leading-24 font-bold uppercase';
export const notSubscribedClasses =
  'py-4 px-[30px] bg-a-blue hover:bg-a-light-blue eurostile text-17 leading-24 font-bold uppercase';
export const subscribedClasses =
  'py-4 px-[30px] bg-a-blue hover:bg-a-light-blue eurostile text-17 leading-24 font-bold uppercase';
export const purchaseOptionsModalClasses =
  'py-[16px] px-[24px] !h-[56px] lg:!w-[248px] bg-a-blue hover:bg-a-light-blue eurostile text-17 leading-24 font-bold uppercase';

export const productGridItemButtonText = 'Buy';
export const collectionPageButtonText = 'Get collection';
export const productModalButtonText = 'Buy asset';
export const nonSubProductModalButtonText = 'get access now';
export const notSubscribedButtonText = 'Subscribe & get access';
export const freeProductButtonText = 'Get access'

import DetectOutsideClick from '../helpers/DetectOutsideClick';
import ProductPurchasedNotification from './ProductPurchasedNotification';
import CreditPurchaseConfirmBlock from '../shared/CreditPurchaseConfirmBlock';

import { CoinsIcon } from '../../modules/Icons';

const PurchaseButton: React.FC<PurchaseButtonProps> = (props: PurchaseButtonProps) => {
  const {
    mode,
    subscription,
    className,
    purchasedItem,
    user,
    onPurchased,
    totalCreditPrice,
    setSelectedProduct,
    closePermanentPurchaseOptionModal,
    collectionId,
  } = props;

  const { purchase } = purchaseHelpers;
  const router = useRouter();
  const dispatch = useDispatch();

  const [showConfirmBlock, setShowConfirmBlock] = useState(false);
  const [loading, setLoading] = useState(false);

  const getDynamicClasses = (): string => {
    if (subscription && mode === 'product-modal') return productModalClasses
    if (subscription && mode !== 'product-grid-item') return subscribedClasses;
    if (!subscription && mode !== 'product-grid-item') return notSubscribedClasses;
    switch (mode) {
      case 'purchase-options-modal':
        return purchaseOptionsModalClasses;
      case 'product-grid-item':
        return productGridItemClasses;
      case 'collection-page':
        return collectionPageClasses;
      case 'product-modal':
        return productModalClasses;
      default:
        return '';
    }
  };

  const getCollectionId = (): number => {
    return collectionId || purchasedItem.id;
  }

  const getButtonText = () => {
    if (subscription && mode !== 'product-grid-item' && !user?.team?.licensee_profile?.license_id) {
      return determineSubscriberButtonText(purchasedItem.credit_price);
    }
    if (!subscription && mode !== 'product-grid-item' && purchasedItem.free_product) {
      return freeProductButtonText;
    }
    if (!subscription && mode === 'product-modal') {
      return nonSubProductModalButtonText
    }
    if (!subscription && mode !== 'product-grid-item') {
      return notSubscribedButtonText;
    }
    switch (mode) {
      case 'product-grid-item':
        return productGridItemButtonText;
      case 'collection-page':
        return determineSubscriberButtonText(purchasedItem.credit_price);
      case 'purchase-options-modal':
        return determineSubscriberButtonText(purchasedItem.credit_price);
      case 'product-modal':
        return productModalButtonText;
      default:
        return '';
    }
  };

  const determineSubscriberButtonText = (creditPrice: number) => {
    return (
      <div className='flex gap-[10px]'>
        <CoinsIcon className="stroke-white w-[20px]"/>
        <span className='whitespace-nowrap'>Buy for {creditPrice} credits</span>
      </div>
    )
  }

  const outsideClickHandler = () => {
    setShowConfirmBlock(false);
  };

  const toggleShowConfirmBlock = () => {
    setShowConfirmBlock(() => !showConfirmBlock);
  };

  const checkIfEnoughCredits = () => {
    if (subscription.current_credit_balance < purchasedItem.credit_price) {
      dispatch(showModal('not-enough-credits', purchasedItem.credit_price - subscription.current_credit_balance))

      if (closePermanentPurchaseOptionModal) {
        closePermanentPurchaseOptionModal();
      }

      return;
    }
    toggleShowConfirmBlock();
  }

  const handlePurchase = async () => {
    if (loading) return;
    if (subscription.current_credit_balance < purchasedItem.credit_price) {
      dispatch(showModal('not-enough-credits', purchasedItem.credit_price - subscription.current_credit_balance))

      if (closePermanentPurchaseOptionModal) {
        closePermanentPurchaseOptionModal();
      }

      return;
    }
    setShowConfirmBlock(false);
    setLoading(true);
    try {
      await purchase({ ...purchasedItem, collection_id: getCollectionId() }, user, router.query[ALGOLIA_QUERY_PARAMS.QUERY_ID] as string);
      dispatch(
        addNotification({
          body: <ProductPurchasedNotification purchasedItem={purchasedItem} />,
          customUi: true
        })
      );
      dispatch(decrementAvailableCredits(purchasedItem.credit_price));
      if (onPurchased) onPurchased();
    } catch (e) {
      setLoading(false);
      dispatch(
        addMessage({
          type: 'error',
          body: e.message
        })
      );
    }
  };

  const goToPricingPage = () => {
    if (mode !== 'product-grid-item') return router.push('/pricing');
    if (setSelectedProduct) setSelectedProduct(purchasedItem)
  };

  const clickHandler = () => {
    subscription && mode !== 'product-grid-item' ? checkIfEnoughCredits() : goToPricingPage();
  };

  return (
    <DetectOutsideClick onOutsideClick={outsideClickHandler}>
      <div className={`md:relative ${className || ''}`} cy-test-id="purchase-button-wrapper">
        <div className='flex flex-col items-center w-full'>
          <div className='w-full mb-[20px]'>
            <button
              className={`flex justify-center items-center text-white rounded-[5px] w-full disabled:opacity-50 disabled:cursor-default ${getDynamicClasses()}`}
              cy-test-id="purchase-button"
              onClick={clickHandler}
              disabled={loading}>
              {getButtonText()}
            </button>
            {(totalCreditPrice - purchasedItem.credit_price > 0) && subscription && mode === 'collection-page' && !purchasedItem.free_for_member && !router.asPath.includes('/3d/') &&
              <span className='flex justify-center gap-1 leading-[20px] mt-[12px] text-[14px] text-gray-600'>
                Save <span className='text-white'>{totalCreditPrice - purchasedItem.credit_price}</span> credits with the full collection
              </span>
            }
          </div>
          <CreditPurchaseConfirmBlock
            purchasedItem={purchasedItem}
            showConfirmBlock={showConfirmBlock}
            onConfirm={handlePurchase}
            className="mt-[-18px]"
          />
        </div>
      </div>
    </DetectOutsideClick>
  );
};

const mapStateToProps = (state: ApplicationState): PurchaseButtonStateProps => ({
  subscription: state.auth.subscription as SubscriptionDetail,
  user: state.auth.user as User
});

export default connect(mapStateToProps)(PurchaseButton);
